import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from 'common/Button/Button';
import './EcdsCta.css';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Місток на реєстрацію в ECDS одразу під Hero.
 *
 * Ціни — фіксований список зі словами замовника, а не з CMS: це три ставки
 * членського внеску, які міняються нечасто і узгоджуються окремим рішенням,
 * не через адмінку контенту сайту. Посилання на оплату веде на інший
 * застосунок (e-phan), тому це звичайне посилання, а не роут SPA.
 */
const PAYMENT_URL = 'https://app.e-phan.com/user-page/registry-join';

const FEES = [
  { ua: 'Членський внесок танцюриста «Активний»', en: 'Dancer membership fee "Active"', amount: 950 },
  { ua: 'Членський внесок танцюриста «Початківець»', en: 'Dancer membership fee "Beginner"', amount: 650 },
  { ua: 'Членський внесок судді', en: 'Judge membership fee', amount: 1600 },
];

export default function EcdsCta() {
  const language = useLanguage();
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const openerRef = useRef(null);
  const dialogRef = useRef(null);

  // Esc, блокування прокрутки тіла й повернення фокуса — той самий прийом,
  // що й у модалці людини в керівництві (ADR-0008).
  useEffect(() => {
    if (!isPricingOpen) return undefined;

    document.body.classList.add('is-locked');

    function handleKeyDown(event) {
      if (event.key === 'Escape') closeModal();
    }

    document.addEventListener('keydown', handleKeyDown);
    dialogRef.current?.focus({ preventScroll: true });

    return () => {
      document.body.classList.remove('is-locked');
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPricingOpen]);

  function closeModal() {
    setIsPricingOpen(false);
    openerRef.current?.focus({ preventScroll: true });
  }

  return (
    <section className='ecds-cta section--wash'>
      <div className='container ecds-cta-inner'>
        <p className='ecds-cta-text'>
          {language === 'en'
            ? 'SGOSTU is the official representative of the European Championship Dance Sport register ECDS. Register, pay the annual membership fee, and build a career in the European dance community.'
            : 'СГОСТУ — офіційний представник європейського реєстру танцювального спорту ECDS. Зареєструйся, оплати річний внесок та будуй кар’єру в європейській танцювальній спільноті.'}
        </p>

        <div className='ecds-cta-actions'>
          <Button
            variant='secondary'
            ref={openerRef}
            buttonText={language === 'en' ? 'View prices' : 'Переглянути ціни'}
            onClick={() => setIsPricingOpen(true)}
          />
          <a className='btn btn--primary' href={PAYMENT_URL} target='_blank' rel='noreferrer'>
            {language === 'en' ? 'Proceed to payment' : 'Перейти до оплати'}
          </a>
        </div>
      </div>

      {/* Портал у document.body — та сама причина, що й у модалці людини:
          секція не повинна нести stacking-контекст, що замикає fixed-нащадка. */}
      {isPricingOpen &&
        createPortal(
          <div className='ecds-modal-overlay' onClick={closeModal}>
            <div
              className='ecds-modal-content'
              role='dialog'
              aria-modal='true'
              aria-labelledby='ecds-fee-title'
              ref={dialogRef}
              tabIndex={-1}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type='button'
                className='ecds-modal-close'
                aria-label={language === 'en' ? 'Close' : 'Закрити'}
                onClick={closeModal}
              >
                <svg width='20' height='20' viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M6 6l12 12M18 6L6 18' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
                </svg>
              </button>

              <h2 className='ecds-modal-title' id='ecds-fee-title'>
                {language === 'en' ? 'Membership fees' : 'Розмір членських внесків'}
              </h2>

              <ul className='ecds-fee-list'>
                {FEES.map((fee) => (
                  <li className='ecds-fee-row' key={fee.ua}>
                    <span>{language === 'en' ? fee.en : fee.ua}</span>
                    <span className='ecds-fee-amount tnum'>{fee.amount} грн</span>
                  </li>
                ))}
              </ul>

              <a className='btn btn--primary btn--block' href={PAYMENT_URL} target='_blank' rel='noreferrer'>
                {language === 'en' ? 'Proceed to payment' : 'Перейти до оплати'}
              </a>
            </div>
          </div>,
          document.body
        )}
    </section>
  );
}
